import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm, 
  Row, 
  Col,
  Tag,
  Tooltip,
  Drawer,
  Descriptions,
  Grid,
  Pagination,
  Upload,
  Divider,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  UploadOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

interface Device {
  id: number;
  device_sn: string;
  device_key: string;
  device_category: string;
  device_type: string;
  device_name: string;
  sim_card_number: string;
  template: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

interface ImportResult {
  success: number;
  skipped: number;
  failed: number;
  errors: Array<{ row: number; reason: string }>;
}

const DeviceManagement: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [messageApi, contextHolder] = message.useMessage();
  
  const [data, setData] = useState<Device[]>([]);
  const [originalData, setOriginalData] = useState<Device[]>([]); // 保存原始数据
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [importData, setImportData] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // 设备业务类型选项
  const deviceBusinessTypeOptions = [
    { label: '打印', value: '打印' },
    { label: '扫描', value: '扫描' }
  ];

  // 设备类型选项
  const deviceTypeOptions = [
    { label: '云打印机', value: '云打印机' },
    { label: '云标签机', value: '云标签机' }
  ];

  // 文件上传处理
  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const data = lines.slice(1).filter(line => line.trim()).map((line, index) => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((header, i) => {
            row[header] = values[i] || '';
          });
          row._rowIndex = index + 1;
          return row;
        });

        // 根据字段名称智能推断映射关系
        const defaultMapping: Record<string, string> = {};
        const usedTargetFields = new Set<string>();
        
        headers.forEach(header => {
          const headerLower = header.toLowerCase();
          let targetField = '';
          
          if (headerLower.includes('设备序列号') || headerLower.includes('device_sn') || headerLower.includes('序列号')) {
            targetField = 'device_sn';
          } else if (headerLower.includes('设备密钥') || headerLower.includes('device_key') || headerLower.includes('密钥')) {
            targetField = 'device_key';
          } else if (headerLower.includes('设备类别') || headerLower.includes('device_category') || headerLower.includes('类别')) {
            targetField = 'device_category';
          } else if (headerLower.includes('设备类型') || headerLower.includes('device_type') || headerLower.includes('类型')) {
            targetField = 'device_type';
          } else if (headerLower.includes('设备名称') || headerLower.includes('device_name') || headerLower.includes('名称')) {
            targetField = 'device_name';
          } else if (headerLower.includes('sim卡号') || headerLower.includes('sim_card_number') || headerLower.includes('sim')) {
            targetField = 'sim_card_number';
          } else if (headerLower.includes('模板') || headerLower.includes('template')) {
            targetField = 'template';
          } else if (headerLower.includes('状态') || headerLower.includes('status')) {
            targetField = 'status';
          }
          
          if (targetField && usedTargetFields.has(targetField)) {
            targetField = '';
          }
          
          if (targetField) {
            defaultMapping[header] = targetField;
            usedTargetFields.add(targetField);
          } else {
            defaultMapping[header] = '';
          }
        });

        setFieldMapping(defaultMapping);
        setImportData(data);
        setImportModalVisible(true);
      } catch (error) {
        messageApi.error('文件解析失败');
      }
    };
    reader.readAsText(file);
    return false;
  }, [messageApi]);

  // 执行导入
  const handleImport = useCallback(async () => {
    try {
      const mappedData = importData.map((row, index) => {
        const mapped: any = {};
        Object.keys(fieldMapping).forEach(sourceField => {
          const targetField = fieldMapping[sourceField];
          if (targetField && targetField !== 'skip') {
            mapped[targetField] = row[sourceField];
          }
        });
        mapped._rowIndex = index + 1;
        return mapped;
      });

      if (mappedData.length === 0) {
        messageApi.error('没有数据可以导入');
        return;
      }

      // 模拟导入过程
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;
      const errors: Array<{ row: number; reason: string }> = [];

      for (const row of mappedData) {
        if (!row.device_sn || !row.device_key || !row.device_name) {
          errorCount++;
          errors.push({
            row: row._rowIndex,
            reason: '必填字段不完整（设备序列号、设备密钥、设备名称为必填）'
          });
        } else if (data.some(device => device.device_sn === row.device_sn)) {
          skipCount++;
          errors.push({
            row: row._rowIndex,
            reason: '重复记录（设备序列号已存在）'
          });
        } else {
          successCount++;
        }
      }

      const result: ImportResult = {
        success: successCount,
        skipped: skipCount,
        failed: errorCount,
        errors: errors
      };

      setImportResult(result);
      
      if (successCount > 0) {
        messageApi.success(`导入完成！成功: ${successCount} 条`);
      }
    } catch (error) {
      console.error('导入过程中发生错误:', error);
      messageApi.error('导入失败');
    }
  }, [fieldMapping, importData, data, messageApi]);


  // 获取设备列表
  const fetchData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/devices?current=${current}&pageSize=${pageSize}&${new URLSearchParams(params)}`);
      const result = await response.json();
      if (result.success) {
        const deviceData = result.data.rows || result.data;
        setData(deviceData);
        setOriginalData(deviceData); // 保存原始数据
        setTotal(result.data.total || result.total || 0);
      } else {
        messageApi.error(result.message || '获取数据失败');
      }
    } catch (error) {
      console.error('获取设备列表失败:', error);
      messageApi.error('网络错误');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, messageApi]);

  // 初始加载
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 搜索处理
  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrent(1);
    
    if (value.trim()) {
      // 如果有搜索内容，从原始数据中过滤
      const filteredData = originalData.filter(device => 
        device.device_sn?.toLowerCase().includes(value.toLowerCase()) ||
        device.device_name?.toLowerCase().includes(value.toLowerCase()) ||
        device.device_type?.toLowerCase().includes(value.toLowerCase()) ||
        device.device_category?.toLowerCase().includes(value.toLowerCase()) ||
        device.sim_card_number?.toLowerCase().includes(value.toLowerCase())
      );
      setData(filteredData);
      setTotal(filteredData.length);
    } else {
      // 如果搜索框为空，恢复原始数据
      setData(originalData);
      setTotal(originalData.length);
    }
  }, [originalData]);



  // 打开新增/编辑模态框
  const showModal = useCallback((device?: Device) => {
    setEditingDevice(device || null);
    setModalVisible(true);
    if (device) {
      // 编辑现有设备：显示当前状态
      form.setFieldsValue({
        ...device,
        status: device.status || '未绑定'
      });
    } else {
      // 新增设备：设置默认状态
      form.resetFields();
      form.setFieldsValue({
        device_type: '打印',
        device_category: '云打印机',
        status: '未绑定'
      });
    }
  }, [form]);

  // 关闭模态框
  const handleCancel = useCallback(() => {
    setModalVisible(false);
    setEditingDevice(null);
    form.resetFields();
  }, [form]);

  // 提交表单
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      if (editingDevice) {
        // 更新设备
        const response = await fetch(`/api/devices/${editingDevice.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        
        if (result.success) {
          messageApi.success('设备更新成功');
          handleCancel();
          fetchData();
        } else {
          messageApi.error(result.message || '更新失败');
        }
      } else {
        // 新增设备
        const response = await fetch('/api/devices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        
        if (result.success) {
          messageApi.success('设备添加成功');
          handleCancel();
          fetchData();
        } else {
          messageApi.error(result.message || '添加失败');
        }
      }
    } catch (error) {
      console.error('提交失败:', error);
      messageApi.error('操作失败');
    }
  }, [editingDevice, form, handleCancel, fetchData, messageApi]);

  // 提交表单并绑定
  const handleSubmitAndBind = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      if (editingDevice) {
        // 更新设备并绑定
        const response = await fetch(`/api/devices/${editingDevice.id}/bind`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        
        if (result.success) {
          messageApi.success(result.message || '设备更新并绑定成功');
          handleCancel();
          fetchData();
        } else {
          messageApi.error(result.message || '更新并绑定失败');
        }
      } else {
        // 新增设备并绑定
        const response = await fetch('/api/devices/bind', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });
        const result = await response.json();
        
        if (result.success) {
          messageApi.success(result.message || '设备创建并绑定成功');
          handleCancel();
          fetchData();
        } else {
          messageApi.error(result.message || '创建并绑定失败');
        }
      }
    } catch (error) {
      console.error('提交并绑定失败:', error);
      messageApi.error('操作失败');
    }
  }, [editingDevice, form, handleCancel, fetchData, messageApi]);

  // 删除设备
  const handleDelete = useCallback(async (id: number) => {
    try {
      const response = await fetch(`/api/devices/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      
      if (result.success) {
        messageApi.success('删除成功');
        fetchData();
      } else {
        messageApi.error(result.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      messageApi.error('删除失败');
    }
  }, [fetchData, messageApi]);

  // 查看设备详情
  const showDetail = useCallback((device: Device) => {
    setSelectedDevice(device);
    setDrawerVisible(true);
  }, []);



  // 表格列定义
  const columns = useMemo(() => [
    {
      title: '设备编号',
      dataIndex: 'device_sn',
      key: 'device_sn',
      render: (text: string) => <Text strong>{text}</Text>,
      fixed: 'left' as const,
      width: isMobile ? 100 : 150
    },
    {
      title: '设备名称',
      dataIndex: 'device_name',
      key: 'device_name',
      width: isMobile ? 120 : 150,
      render: (name: string) => name || '-'
    },
    {
      title: '设备类型',
      dataIndex: 'device_category',
      key: 'device_category',
      width: isMobile ? 80 : 100
    },
    {
      title: '设备业务类型',
      dataIndex: 'device_type',
      key: 'device_type',
      width: isMobile ? 100 : 120
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === '已绑定' ? 'green' : 'orange'}>
          {status}
        </Tag>
      ),
      width: isMobile ? 60 : 80
    },
    {
      title: 'SIM卡号',
      dataIndex: 'sim_card_number',
      key: 'sim_card_number',
      render: (sim: string) => sim || '-',
      width: isMobile ? 100 : 120
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: isMobile ? 120 : 150,
      render: (_: any, record: Device) => (
        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
          <Tooltip title="查看详情">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => showDetail(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size={isMobile ? 'small' : 'middle'}
              onClick={() => showModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个设备吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                size={isMobile ? 'small' : 'middle'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ], [isMobile, showDetail, showModal, handleDelete]);

  // 分页变化
  const handlePageChange = (page: number, size: number) => {
    setCurrent(page);
    setPageSize(size);
  };

  // 渲染设备卡片（移动端）
  const renderDeviceCard = useCallback((device: Device) => (
    <Card 
      key={device.id} 
      size="small" 
      style={{ marginBottom: 12 }}
      actions={[
        <Tooltip title="查看详情" key="view">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => showDetail(device)}
          >
            详情
          </Button>
        </Tooltip>,
        <Tooltip title="编辑" key="edit">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            size="small"
            onClick={() => showModal(device)}
          >
            编辑
          </Button>
        </Tooltip>,
        <Popconfirm
          title="确定要删除这个设备吗？"
          onConfirm={() => handleDelete(device.id)}
          okText="确定"
          cancelText="取消"
          key="delete"
        >
          <Tooltip title="删除">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Tooltip>
        </Popconfirm>
      ]}
    >
      <Row gutter={[8, 6]}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">设备编号：</Text>
            <div style={{ textAlign: 'right' }}>
              <Text strong style={{ fontSize: 16 }}>{device.device_sn}</Text>
              <Tag color={device.status === '已绑定' ? 'green' : 'orange'} style={{ marginLeft: 6 }}>
                {device.status}
              </Tag>
            </div>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">设备名称：</Text>
            <Text style={{ textAlign: 'right' }}>{device.device_name || '未设置'}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">设备类型：</Text>
            <Text style={{ textAlign: 'right' }}>{device.device_category}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">设备业务类型：</Text>
            <Text style={{ textAlign: 'right' }}>{device.device_type}</Text>
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary">SIM卡号：</Text>
            <Text copyable style={{ textAlign: 'right' }}>{device.sim_card_number || '未设置'}</Text>
          </div>
        </Col>

      </Row>
    </Card>
  ), [showDetail, showModal, handleDelete]);

  return (
    <div style={{ padding: isMobile ? '16px' : '12px' }}>
      {contextHolder}
      <Card>

        {/* 页面头部 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={8}>
              <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>设备管理</Title>
            </Col>
            <Col xs={24} sm={24} md={16}>
              <Space 
                direction={isMobile ? 'vertical' : 'horizontal'} 
                style={{ width: '100%', justifyContent: isMobile ? 'stretch' : 'flex-end' }}
                size="small"
              >
                <Input
                  placeholder="搜索设备编号、名称或类型"
                  prefix={<SearchOutlined />}
                  suffix={
                    searchText ? (
                      <Button
                        type="text"
                        size="small"
                        onClick={() => {
                          setSearchText('');
                          setData(originalData);
                          setTotal(originalData.length);
                        }}
                        style={{ border: 'none', padding: '0 4px' }}
                      >
                        ✕
                      </Button>
                    ) : null
                  }
                  value={searchText}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchText(value);
                    handleSearch(value);
                  }}
                  style={{ width: isMobile ? '100%' : 250 }}
                />
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={fetchData}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  刷新
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  批量导入
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={() => showModal()}
                  style={{ width: isMobile ? '100%' : 'auto' }}
                >
                  新增设备
                </Button>
              </Space>
            </Col>
          </Row>
          {isMobile && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">
                共 {total} 个设备（当前页显示 {data.length} 个）
              </Text>
            </div>
          )}
        </div>

        {isMobile ? (
          // 移动端卡片布局
          <div>
            {data.map(renderDeviceCard)}
            {data.length === 0 && !loading && (
              <Card style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">暂无设备数据</Text>
              </Card>
            )}
            
            {/* 移动端分页 */}
            {total > pageSize && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Pagination
                  current={current}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger={false}
                  showQuickJumper={true}
                  showTotal={(total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                  onChange={handlePageChange}
                  size="small"
                />
              </div>
            )}
          </div>
        ) : (
          // 桌面端表格布局
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              current: current,
              pageSize: pageSize,
              total: total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total: number, range: [number, number]) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: handlePageChange,
              onShowSizeChange: handlePageChange
            }}
            size="middle"
          />
        )}
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingDevice ? '编辑设备' : '新增设备'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? '98%' : 600}
        style={{ top: isMobile ? 20 : 100 }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            device_category: '云打印机',
            status: '未绑定'
          }}
        >
          <Form.Item
            name="device_sn"
            label="设备编号"
            rules={[{ required: true, message: '请输入设备编号' }]}
          >
            <Input placeholder="请输入设备编号" />
          </Form.Item>

          <Form.Item
            name="device_key"
            label="设备密钥"
            rules={[{ required: true, message: '请输入设备密钥' }]}
          >
            <Input placeholder="请输入设备密钥" />
          </Form.Item>

          <Form.Item
            name="device_type"
            label="设备业务类型"
            rules={[{ required: true, message: '请选择设备业务类型' }]}
          >
            <Select placeholder="请选择设备业务类型">
              {deviceBusinessTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="device_category"
            label="设备类型"
            rules={[{ required: true, message: '请选择设备类型' }]}
          >
            <Select placeholder="请选择设备类型">
              {deviceTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="device_name"
            label="设备名称"
          >
            <Input placeholder="请输入设备名称" />
          </Form.Item>

          <Form.Item
            name="status"
            label="设备状态"
          >
            <Input 
              placeholder="系统自动设置" 
              disabled 
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            name="sim_card_number"
            label="SIM卡号"
          >
            <Input placeholder="请输入SIM卡号" />
          </Form.Item>

          <Form.Item
            name="template"
            label="打印模板"
          >
            <TextArea 
              rows={4} 
              placeholder="请输入打印模板" 
              showCount 
              maxLength={500}
            />
          </Form.Item>
          
          {/* 表单操作按钮 */}
          <div style={{ marginTop: '24px', textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancel}>
                取消
              </Button>
              <Button type="primary" onClick={handleSubmit}>
                {editingDevice ? '更新' : '创建'}
              </Button>
              <Button type="primary" onClick={handleSubmitAndBind}>
                {editingDevice ? '更新并绑定' : '创建并绑定'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 设备详情抽屉 */}
      <Drawer
        title="设备详情"
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? '100%' : 500}
        height={isMobile ? '85%' : undefined}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
      >
        {selectedDevice && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="设备编号">{selectedDevice.device_sn}</Descriptions.Item>
            <Descriptions.Item label="设备密钥">{selectedDevice.device_key}</Descriptions.Item>
            <Descriptions.Item label="设备类型">{selectedDevice.device_category}</Descriptions.Item>
            <Descriptions.Item label="设备业务类型">{selectedDevice.device_type}</Descriptions.Item>
            <Descriptions.Item label="设备名称">{selectedDevice.device_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="SIM卡号">{selectedDevice.sim_card_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="打印模板">{selectedDevice.template || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedDevice.status === '已绑定' ? 'success' : 'warning'}>
                {selectedDevice.status}
              </Tag>
            </Descriptions.Item>
            {selectedDevice.created_at && (
              <Descriptions.Item label="创建时间">{selectedDevice.created_at}</Descriptions.Item>
            )}
            {selectedDevice.updated_at && (
              <Descriptions.Item label="更新时间">{selectedDevice.updated_at}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      {/* 批量导入模态框 */}
      <Modal
        title="批量导入设备"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            beforeUpload={handleFileUpload}
            accept=".csv"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>选择CSV文件</Button>
          </Upload>
          <span style={{ marginLeft: '8px', color: '#666' }}>
            支持CSV格式，第一行为表头
          </span>
        </div>

        {importData.length > 0 && (
          <>
            <Divider>字段映射</Divider>
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '12px' }}>请将源文件字段映射到目标字段</h4>
              
              <div style={{ 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f0f8ff',
                border: '1px solid #91d5ff',
                borderRadius: '4px'
              }}>
                <Text strong style={{ color: '#1890ff' }}>字段映射说明：</Text>
                <Text style={{ marginLeft: '8px' }}>
                  请将源文件字段映射到对应的目标字段，选择"不导入"可跳过不需要的字段
                </Text>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                {Object.keys(fieldMapping).map((field) => {
                  const targetField = fieldMapping[field];
                  
                  return (
                    <div key={field} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '8px 0',
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <div style={{ 
                        flex: '1',
                        padding: '8px 12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        border: '1px solid #d9d9d9',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{ fontWeight: '500', color: '#1890ff' }}>{field}</span>
                      </div>
                      
                      <div style={{ 
                        fontSize: '18px',
                        color: '#1890ff',
                        fontWeight: 'bold',
                        padding: '0 8px'
                      }}>
                        →
                      </div>
                      
                      <div style={{ flex: '1' }}>
                        <Select
                          value={fieldMapping[field]}
                          onChange={(value) => setFieldMapping({ ...fieldMapping, [field]: value })}
                          style={{ width: '100%' }}
                          placeholder="请选择对应字段"
                        >
                          <Option value="device_sn">设备序列号</Option>
                          <Option value="device_key">设备密钥</Option>
                          <Option value="device_category">设备类别</Option>
                          <Option value="device_type">设备类型</Option>
                          <Option value="device_name">设备名称</Option>
                          <Option value="sim_card_number">SIM卡号</Option>
                          <Option value="template">打印模板</Option>
                          <Option value="status">状态</Option>
                          <Option value="skip">不导入</Option>
                        </Select>
                      </div>
                      
                      <div style={{ 
                        width: '60px',
                        textAlign: 'center'
                      }}>
                        {targetField && (
                          <Tag color={
                            targetField === 'skip' ? 'default' : 'green'
                          }>
                            {targetField === 'skip' ? '不导入' : '导入'}
                          </Tag>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <Divider>数据预览（前5条）</Divider>
            <Table
              dataSource={importData.slice(0, 5)}
              columns={Object.keys(fieldMapping).map(field => ({
                title: field,
                dataIndex: field,
                key: field,
              }))}
              pagination={false}
              size="small"
            />

            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setImportModalVisible(false)}>
                  取消
                </Button>
                <Button type="primary" onClick={handleImport}>
                  开始导入
                </Button>
              </Space>
            </div>
          </>
        )}

        {/* 导入结果 */}
        {importResult && (
          <>
            <Divider>导入结果</Divider>
            
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={8}>
                <Statistic 
                  title="成功导入" 
                  value={importResult.success} 
                  valueStyle={{ color: '#3f8600' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="跳过记录" 
                  value={importResult.skipped} 
                  valueStyle={{ color: '#faad14' }}
                  suffix="条记录"
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="失败记录" 
                  value={importResult.failed} 
                  valueStyle={{ color: '#cf1322' }}
                  suffix="条记录"
                />
              </Col>
            </Row>
            
            {importResult.errors.length > 0 && (
              <div style={{ 
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px'
              }}>
                <h4 style={{ color: '#cf1322', marginBottom: '12px' }}>详细说明：</h4>
                
                {importResult.errors.filter(error => error.reason.includes('重复记录')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#faad14' }}>跳过记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      系统检测到重复记录（设备序列号已存在），已自动跳过
                    </Text>
                  </div>
                )}
                
                {importResult.errors.filter(error => error.reason.includes('必填字段')).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <Text strong style={{ color: '#cf1322' }}>失败记录原因：</Text>
                    <Text style={{ marginLeft: '8px' }}>
                      必填字段数据不完整，无法导入
                    </Text>
                  </div>
                )}
                
                <div style={{ marginTop: '12px' }}>
                  <Text strong>具体错误：</Text>
                  {importResult.errors.map((error, index) => (
                    <div key={index} style={{ 
                      color: '#cf1322', 
                      marginBottom: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#fff',
                      borderRadius: '2px'
                    }}>
                      <Text strong>第{error.row}行：</Text> {error.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Space size="large">
                <Button 
                  type="primary" 
                  onClick={() => {
                    setImportModalVisible(false);
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  完成并关闭
                </Button>
                <Button 
                  onClick={() => {
                    setImportData([]);
                    setFieldMapping({});
                    setImportResult(null);
                  }}
                >
                  重新导入
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default DeviceManagement;
